import { CalendarDays, Code2, Film, LayoutGrid, LogOut, Menu, Settings, Users, X } from 'lucide-react'
import { useState } from 'react'
import type { ReactNode } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { useAuth } from '../auth/useAuth'
import { Logo } from '../components/Shell'
import { ThemeToggle } from '../ui/ThemeToggle'

const NAV = [
  { to: '/', label: 'Overview', Icon: LayoutGrid, end: true },
  { to: '/rooms', label: 'Meetings', Icon: CalendarDays },
  { to: '/recordings', label: 'Recordings', Icon: Film },
  { to: '/developers', label: 'Developers', Icon: Code2 },
  { to: '/team', label: 'Team', Icon: Users, adminOnly: true },
  { to: '/settings', label: 'Settings', Icon: Settings },
]

function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  const { isAdmin } = useAuth()
  return (
    <nav className="flex flex-col gap-0.5">
      {NAV.filter((item) => !item.adminOnly || isAdmin).map(({ to, label, Icon, end }, i) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          className={({ isActive }) =>
            `group flex items-center gap-3 border-l-2 px-3 py-2 text-[15px] transition-colors ${
              isActive
                ? 'border-ink bg-subtle font-medium text-ink'
                : 'border-transparent text-ink-2 hover:bg-subtle/60 hover:text-ink'
            }`
          }
        >
          <Icon className="h-4 w-4" />
          <span className="flex-1">{label}</span>
          <span className="font-mono text-[10px] text-ink-3">{String(i + 1).padStart(2, '0')}</span>
        </NavLink>
      ))}
    </nav>
  )
}

function UserBlock() {
  const { user, logout } = useAuth()
  return (
    <div className="flex flex-col gap-4 border-t border-line pt-4">
      <div className="flex items-center justify-between">
        <span className="label-mono">Theme</span>
        <ThemeToggle />
      </div>
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center bg-ink font-display text-lg font-bold text-bg uppercase">
          {user?.display_name.slice(0, 1)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-medium">{user?.display_name}</p>
          <p className="truncate text-[12px] text-ink-3">
            {user?.role === 'superadmin' ? 'Super admin · ' : user?.role === 'admin' ? 'Admin · ' : ''}
            {user?.email}
          </p>
        </div>
        <button onClick={() => void logout()} className="p-1.5 text-ink-3 hover:text-ink" title="Sign out" aria-label="Sign out">
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

export function AppShell() {
  const [open, setOpen] = useState(false)

  return (
    <div className="min-h-full lg:grid lg:grid-cols-[260px_1fr]">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen flex-col justify-between border-r border-line bg-surface px-4 py-6 lg:flex">
        <div className="flex flex-col gap-8">
          <div className="px-2">
            <Logo />
          </div>
          <NavItems />
        </div>
        <UserBlock />
      </aside>

      {/* Mobile top bar + drawer */}
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-line bg-bg/90 px-4 py-3 backdrop-blur lg:hidden">
        <Logo />
        <button onClick={() => setOpen(true)} className="p-2 text-ink" aria-label="Open menu">
          <Menu className="h-5 w-5" />
        </button>
      </header>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} aria-label="Close menu" />
          <aside className="rise-in absolute inset-y-0 right-0 flex w-[min(20rem,85%)] flex-col justify-between bg-surface px-4 py-5">
            <div className="flex flex-col gap-6">
              <div className="flex items-center justify-between px-2">
                <Logo />
                <button onClick={() => setOpen(false)} className="p-2" aria-label="Close menu">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <NavItems onNavigate={() => setOpen(false)} />
            </div>
            <UserBlock />
          </aside>
        </div>
      )}

      <main className="min-w-0">
        <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-8 sm:py-12">
          <Outlet />
        </div>
      </main>
    </div>
  )
}

/** Standard page header: mono eyebrow, big condensed title, muted lead, actions. */
export function PageHeader({
  eyebrow,
  title,
  lead,
  actions,
}: {
  eyebrow: string
  title: ReactNode
  lead?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="rise-in mb-10 flex flex-wrap items-end justify-between gap-6">
      <div className="max-w-2xl">
        <p className="label-mono mb-3">{eyebrow}</p>
        <h1 className="text-5xl font-bold sm:text-6xl">{title}</h1>
        {lead && <p className="mt-3 text-[17px] leading-relaxed text-ink-2">{lead}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  )
}
